import React, { useEffect, useMemo, useState } from 'react';
import { Download, Edit, Eye, Filter, Plus, Search, Trash2, X } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { Tooltip } from '../../components/ui/Tooltip';
import { ColumnCustomizer } from '../../components/ColumnCustomizer';
import { PermissionGate } from '../../components/PermissionGate';
import { TeacherDetailModal } from '../../components/training/TeacherDetailModal';
import { TeacherFormModal } from '../../components/training/TeacherFormModal';
import { useSearch } from '../../hooks/useSearch';
import { usePagination } from '../../hooks/usePagination';
import { useTeachers } from '../../hooks/useTeachers';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { teachersStore } from '../../services/teachersStore';
import { trainingCategories, trainingProducts } from '../../data/trainingProducts';
import { formatEuro } from '../../utils/currency';
import { teacherCategories, teacherFullName } from '../../utils/teachers';
import { Teacher } from '../../types/teacher';
const columnsConfig = [{
  key: 'name',
  label: 'Docente'
}, {
  key: 'contacts',
  label: 'Contatti'
}, {
  key: 'zones',
  label: 'Zone coperte'
}, {
  key: 'products',
  label: 'Corsi erogabili'
}, {
  key: 'categories',
  label: 'Macrocategorie'
}, {
  key: 'hourlyRate',
  label: 'Tariffa oraria'
}, {
  key: 'courses',
  label: 'Corsi assegnati'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function TeachersPage() {
  const teachers = useTeachers();
  const courses = useTrainingCourses();
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [category, setCategory] = useState('');
  const [productCode, setProductCode] = useState('');
  const [billingMethod, setBillingMethod] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Teacher | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<Teacher | null>(null);
  const assignedCount = (id: string) => courses.filter(c => (c.teachers || []).some(t => t.teacherId === id)).length;
  const rows = useMemo(() => teachers.filter(t => (!category || teacherCategories(t).includes(category)) && (!productCode || t.productCodes.includes(productCode)) && (!billingMethod || t.billingMethod === billingMethod)).map(t => ({
    ...t,
    fullName: teacherFullName(t)
  })).sort((a, b) => a.lastName.localeCompare(b.lastName)), [teachers, category, productCode, billingMethod]);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(rows, ['fullName', 'email', 'fiscalCode', 'zones']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, category, productCode, billingMethod]);
  const activeFilterCount = [category, productCode, billingMethod].filter(Boolean).length;
  const show = (key: string) => visibleColumns.includes(key);
  const shownColumns = columnsConfig.filter(c => show(c.key));
  const openCreate = () => {
    setEditing(null);
    setIsFormOpen(true);
  };
  const openEdit = (t: Teacher) => {
    setViewingId(null);
    setEditing(t);
    setIsFormOpen(true);
  };
  const handleExport = () => {
    const header = ['Nome', 'Cognome', 'CF', 'Email', 'Telefono', 'Zone coperte', 'Corsi erogabili', 'Macrocategorie', 'Tariffa oraria', 'Metodo di fatturazione'];
    const data = searchResult.map(t => [t.firstName, t.lastName, t.fiscalCode, t.email, t.phone, t.zones, t.productCodes.join(', '), teacherCategories(t).join(', '), String(t.hourlyRate), t.billingMethod]);
    const csv = [header, ...data].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'docenti.csv';
    link.click();
    URL.revokeObjectURL(url);
  };
  const deletingCount = deleting ? assignedCount(deleting.id) : 0;
  return <Layout data-id="element-6199" data-mp="3n831wcxz6svh">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-6200" data-mp="3n8cixb11lpp1">
        <div data-id="element-6201" data-mp="3s64kyxveis6y">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6202" data-mp="3s7yxbnslxbzf">Docenti</h1>
          <p className="text-slate-500 mt-1" data-id="element-6203" data-mp="3knwmn93wvfzm">Anagrafica dei docenti dei corsi di formazione.</p>
        </div>
        <div className="flex gap-3" data-id="element-6204" data-mp="3fubw224ndoag">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-6206" data-mp="35xh11t7l260x" />} onClick={handleExport} data-id="element-6205" data-mp="3awae5kiyjv0k">
            Esporta
          </Button>
          <PermissionGate module="training" action="create" data-id="element-6207" data-mp="7g3ctko0m9uy">
            <Button onClick={openCreate} startContent={<Plus className="h-4 w-4" data-id="element-6209" data-mp="2hs1f5i2nram" />} data-id="element-6208" data-mp="7dh43hpe1ob5">
              Nuovo docente
            </Button>
          </PermissionGate>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-6210" data-mp="3tp4ilkiyg4qj">
        <div className="p-4 flex flex-col gap-4" data-id="element-6211" data-mp="3oogjebkkohl8">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6212" data-mp="3onov7ltb1m4z">
            <div className="flex-1" data-id="element-6213" data-mp="2i8xrlacsrn79">
              <Input placeholder="Cerca nome, email, codice fiscale, zona..." startContent={<Search className="h-4 w-4" data-id="element-6215" data-mp="2daw6kjvt9dt5" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-6214" data-mp="2i962iwpd0ift" />
            </div>
            <div className="flex gap-2" data-id="element-6216" data-mp="198lh7c1f7qnc">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6218" data-mp="lktudei97v7t" />} data-id="element-6217" data-mp="vby86aw71a12">
                Filtri{' '}
                {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6219" data-mp="qdmxjxy2qvhs">
                    {activeFilterCount}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="training-teachers" lockedColumns={['name']} data-id="element-6220" data-mp="hktia0imnw3x" />
            </div>
          </div>
          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100" data-id="element-6221" data-mp="3k5gf2nbqr875">
              <Select label="Macrocategoria" value={category} onChange={e => setCategory(e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...trainingCategories.map(c => ({
            value: c,
            label: c
          }))]} data-id="element-6222" data-mp="3g5eeyilnmmtz" />
              <Select label="Corso erogabile" value={productCode} onChange={e => setProductCode(e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...trainingProducts.map(p => ({
            value: p.code,
            label: `${p.name} · ${p.code}`
          }))]} data-id="element-6223" data-mp="2xl2emedj2lm" />
              <Select label="Metodo di fatturazione" value={billingMethod} onChange={e => setBillingMethod(e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Fattura',
            label: 'Fattura'
          }, {
            value: "Ritenuta d'acconto",
            label: "Ritenuta d'acconto"
          }]} data-id="element-6224" data-mp="lnj8iwym2lmm" />
              <div className="col-span-full flex justify-end" data-id="element-6225" data-mp="3heevt0rou5vl">
                <Button variant="flat" color="danger" size="sm" onClick={() => {
              setCategory('');
              setProductCode('');
              setBillingMethod('');
            }} startContent={<X className="h-4 w-4" data-id="element-6227" data-mp="2tmg5panky5ye" />} data-id="element-6226" data-mp="3hbiynennzl87">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6228" data-mp="20xssm2czimvx">
        <div className="overflow-x-auto" data-id="element-6229" data-mp="25xolb5g8erw6">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6230" data-mp="25wlu0wezynfq">
            <thead data-id="element-6231" data-mp="n3280viaeena">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6232" data-mp="n1hn7xlrya2m">
                {shownColumns.map(col => <th key={col.key} className={`px-4 py-4 whitespace-nowrap ${col.key === 'actions' ? 'pr-6' : ''}`} data-id="element-6233" data-mp="e1ck3znm5xu9">
                    {col.label}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6234" data-mp="46kmivu17azh">
              {paginatedData.length === 0 && <tr data-id="element-6235" data-mp="3rhtpatipbe2d">
                  <td colSpan={shownColumns.length} className="px-4 py-16 text-center text-sm text-slate-500" data-id="element-6236" data-mp="3rhactsbtlhvq">
                    Nessun docente corrisponde alla ricerca o ai filtri.
                  </td>
                </tr>}
              {paginatedData.map(t => {
              const count = assignedCount(t.id);
              return <tr key={t.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-6237" data-mp="3cmvttrs9a8h2">
                    {show('name') && <td className="px-4 py-4" data-id="element-6238" data-mp="2g7f6qjg9a141">
                        <button type="button" onClick={() => setViewingId(t.id)} className="text-sm font-medium text-seform-navy hover:text-seform-orange text-left" data-id="element-6239" data-mp="2g6nad6eu3zcp">
                          {t.fullName}
                        </button>
                        <p className="text-xs font-mono text-slate-400 mt-0.5" data-id="element-6240" data-mp="1x8b0tjk0gngl">{t.fiscalCode}</p>
                      </td>}
                    {show('contacts') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6241" data-mp="268po2ogjccmq">
                        {t.email}
                        <span className="block text-xs text-slate-400 mt-0.5" data-id="element-6242" data-mp="3mmpixl0ixc34">{t.phone}</span>
                      </td>}
                    {show('zones') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6243" data-mp="3hnmqd01qcyc1">{t.zones || '-'}</td>}
                    {show('products') && <td className="px-4 py-4 text-sm text-slate-600 max-w-[260px]" data-id="element-6244" data-mp="3u6i92rmy1t4m">
                        <span className="font-mono text-xs" data-id="element-6245" data-mp="3u45nm4hz90mt">{t.productCodes.join(', ')}</span>
                      </td>}
                    {show('categories') && <td className="px-4 py-4" data-id="element-6246" data-mp="3l7wnsoq38005">
                        <div className="flex flex-wrap gap-1.5" data-id="element-6247" data-mp="cpqppxtwug6k">
                          {teacherCategories(t).map(c => <Badge key={c} variant="info" size="sm" data-id="element-6248" data-mp="hok6bz4hunay">
                              {c}
                            </Badge>)}
                        </div>
                      </td>}
                    {show('hourlyRate') && <td className="px-4 py-4 text-sm text-slate-700 tabular-nums whitespace-nowrap" data-id="element-6249" data-mp="vo32runy0a7f">
                        {formatEuro(t.hourlyRate)}/h
                        <span className="block text-xs text-slate-400 mt-0.5" data-id="element-6250" data-mp="1abbfizwwgnli">{t.billingMethod}</span>
                      </td>}
                    {show('courses') && <td className="px-4 py-4 text-sm text-slate-700 tabular-nums" data-id="element-6251" data-mp="10d41opxwqlnq">{count}</td>}
                    {show('actions') && <td className="px-4 py-4 pr-6" data-id="element-6252" data-mp="wbiu9k456zkx">
                        <div className="flex items-center gap-1" data-id="element-6253" data-mp="rd7giyaq84xf">
                          <Tooltip content="Visualizza docente" data-id="element-6254" data-mp="reipdcj142ve">
                            <Button variant="light" size="sm" className="!px-1" aria-label="Visualizza docente" onClick={() => setViewingId(t.id)} data-id="element-6255" data-mp="mkeg2mmajir5">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-6256" data-mp="8o0xtb9xndbm" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="training" action="edit" data-id="element-6257" data-mp="2fb9o0f2z1ag9">
                            <Tooltip content="Modifica docente" data-id="element-6258" data-mp="2k9b6oywhj7r7">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Modifica docente" onClick={() => openEdit(t)} data-id="element-6259" data-mp="2kbnys4vyiyhz">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-6260" data-mp="20las6iyw2b3y" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="training" action="delete" data-id="element-6261" data-mp="2ab3suoxht5tf">
                            <Tooltip content="Elimina docente" data-id="element-6262" data-mp="2ad86hyl71j6w">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Elimina docente" onClick={() => setDeleting(t)} data-id="element-6263" data-mp="1cv856h9tzvc7">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-6264" data-mp="3fgtwtfncl40j" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6265" data-mp="238oe6sdg6fa">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6266" data-mp="g39tjyfo600k" />
        </div>
      </Card>

      <TeacherDetailModal teacherId={viewingId} onClose={() => setViewingId(null)} onEdit={id => {
      const t = teachers.find(x => x.id === id);
      if (t) openEdit(t);
    }} data-id="element-6267" data-mp="2dtb0nuom0gdc" />
      <TeacherFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} teacher={editing} data-id="element-6268" data-mp="27rhllrps3uiv" />

      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} title="Elimina docente" size="md" data-id="element-6269" data-mp="1u0cq91tg1oec">
        {deleting && <div className="space-y-6" data-id="element-6270" data-mp="1yx35xo1c6hz8">
            {deletingCount > 0 ? <p className="text-sm text-slate-700" data-id="element-6271" data-mp="1k0c1v1gpwi3a">
                <strong data-id="element-6272" data-mp="1k3q3g7hk4n9o">{teacherFullName(deleting)}</strong> è assegnato a {deletingCount}{' '}
                {deletingCount === 1 ? 'corso' : 'corsi'}. Rimuovilo prima dai corsi per poterlo eliminare.
              </p> : <p className="text-sm text-slate-700" data-id="element-6273" data-mp="1fz0dez9ky2wo">
                Il docente <strong data-id="element-6274" data-mp="35eq91s9cbdni">{teacherFullName(deleting)}</strong> verrà eliminato dall'anagrafica.
              </p>}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-6275" data-mp="3aeckpx9islu7">
              <Button variant="flat" color="default" onClick={() => setDeleting(null)} data-id="element-6276" data-mp="37ywfc6lffg78">
                {deletingCount > 0 ? 'Chiudi' : 'Annulla'}
              </Button>
              {deletingCount === 0 && <Button color="danger" onClick={() => {
            teachersStore.remove(deleting.id);
            setDeleting(null);
          }} data-id="element-6277" data-mp="2yxy7tebs4qjw">
                  Elimina
                </Button>}
            </div>
          </div>}
      </Modal>
    </Layout>;
}